import { ref, computed, watch, onBeforeUnmount, type Ref } from 'vue'
import { getOptionsList } from '@/helpers/entitiesList'
import { getMinValue } from '@/helpers/util'
import { getInitialTime } from '@/util/timeUtils'
import { activeCallsQueueMap } from '@/composables/useSharedQueueCallsData'
import {
    removeSoundsFromQueueByIdWithHistoryKeep,
    updateSoundCurrentValue,
    updateTimerStateValue
} from '@/composables/useSoundsStoreData'
import {
    billingCdrQueueTypes,
    defaultRowData,
    DefaultRowData,
    ExitCount,
    MaxCallerCount,
    MaxCallersCount,
    MaxWaitTime,
    MaxWaitTimes,
    multiQueueColumnsPropNames,
    getMultiQueueStatTextColor,
    QueueData,
    QueueFromEntities
} from '@/helpers/multiQueueDashboardRedesign'
import { useQueuesThresholdNotifications } from '@/composables/useQueuesThresholdNotifications'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { ADDITIONAL_DATA_KEY, QUEUE_RULE_NO_RULE } from '@/enum/redesignQueueDashboardStatistics'
import { limitThresholdValue, warningThresholdValue } from '@/composables/useSoundsThresholdNotification'
import { WidgetExtended } from '@/types/dashboard'
import i18n from '@/i18n'
import { timeFormatter } from '@/helpers/timeFormatter'

const QUEUE_LIST_KEY = '{|queue_list|}'
const warningStatus = 'warning'
const limitStatus = 'limit'
const idleStatus = 'idle'
const callersBaseNotificationId = 'callers'
const waitTimeNotificationId = 'wait_time'
const waitTimeWarningProperty = 'max warning wait time'
const waitTimeLimitProperty = 'max limit wait time'
const callersWarningProperty = 'max warning callers'
const callersLimitProperty = 'max limit callers'
const notificationTag = 'widget.redesignMultiQueueTableHtml'

export interface QueueRule {
    ID: number | string
    RuleName: string
}

export interface QueueWithRules extends QueueFromEntities {
    QueueRules?: QueueRule[]
}

export interface QueueRuleRow {
    queue_id: number
    queue_name: string
    queue_rule_id: number | string
    queue_rule_name: string
}

function normalizeRuleId (ruleId: unknown) {
    if (ruleId === 0 || ruleId === '0' || !ruleId) return QUEUE_RULE_NO_RULE
    return ruleId
}

/** Stable `row-key` for element-ui `ElTable` in multi-queue widgets */
export function getMultiQueueElTableRowKeyAsRows (row: Record<string, unknown>): string {
    if (row.queue_id == null) {
        return 'unknown-queue-row'
    }
    const queueId = String(row.queue_id)
    const ruleId = row.queue_rule_id
    if (ruleId != null && ruleId !== '') {
        return `${queueId}__${String(ruleId)}`
    }
    return queueId
}

export function resetWaitTimeThresholdTracking (
    maxCurrentQueuesWaitingTime: Ref<MaxWaitTimes>,
    waitingQueueStatus: Ref<string>
) {
    const queueIds = Object.keys(maxCurrentQueuesWaitingTime.value)
    if (!queueIds.length && waitingQueueStatus.value === idleStatus) return

    for (const queueId of queueIds) {
        removeSoundsFromQueueByIdWithHistoryKeep(`${waitTimeNotificationId}${queueId}`)
    }
    maxCurrentQueuesWaitingTime.value = {}
    waitingQueueStatus.value = idleStatus
}

export function resetCallersThresholdTracking (
    maxCurrentQueuesCallerCount: Ref<MaxCallersCount>,
    callersStatus: Ref<string>
) {
    const queueIds = Object.keys(maxCurrentQueuesCallerCount.value)
    if (!queueIds.length && callersStatus.value === idleStatus) return

    for (const queueId of queueIds) {
        removeSoundsFromQueueByIdWithHistoryKeep(`${callersBaseNotificationId}${queueId}`)
    }
    maxCurrentQueuesCallerCount.value = {}
    callersStatus.value = idleStatus
}

export function getMultiQueueElTableRowKeyAsColumns (row: Record<string, unknown>): string {
    const statistic = row.Statistic
    if (typeof statistic === 'string' || typeof statistic === 'number') {
        return String(statistic)
    }
    if (statistic != null && typeof statistic === 'object' && 'value' in statistic) {
        return String((statistic as { value: unknown }).value)
    }
    if (row.__stat != null) {
        return String(row.__stat)
    }
    return 'unknown-columns-row'
}

export function useMultiQueueTableCommon (
    widgetData: Ref<WidgetExtended>,
    queueTableData: Ref<QueueData[]>,
    showStatsInPercentage: Ref<boolean>,
    isRulesMode: Ref<boolean> = ref(false)
) {
    const maxCurrentQueuesCallerCount = ref<MaxCallersCount>({})
    const maxCurrentQueuesWaitingTime = ref<MaxWaitTimes>({})
    const allEntityQueues = ref<QueueWithRules[] | []>([])
    const callersStatus = ref('')
    const waitingQueueStatus = ref('')

    const {
        maximumWarningCallersValueFromSettings,
        maximumLimitCallersValueFromSettings,
        maximumWarningWaitTimeValueFromSettings,
        maximumLimitWaitTimeValueFromSettings,
        maximumWarningCallersSound,
        maximumLimitCallersSound,
        maximumWarningMaxRingTimeSound,
        maximumLimitMaxRingTimeSound,
        setupNotification
    } = useQueuesThresholdNotifications(widgetData)

    const { filteredItems: filteredQueues } = useEntityPositiveNegativeFilters(
        widgetData,
        QUEUE_LIST_KEY,
        allEntityQueues,
        'queue_id'
    )

    const allQueueRules = computed<QueueRuleRow[]>(() => {
        if (!isRulesMode.value || !filteredQueues.value?.length) {
            return []
        }
        return (filteredQueues.value as QueueWithRules[]).reduce<QueueRuleRow[]>((acc, queue) => {
            const rules = queue.QueueRules ?? []
            for (const rule of rules) {
                acc.push({
                    queue_id: queue.queue_id,
                    queue_name: queue.q_name,
                    queue_rule_id: rule.ID,
                    queue_rule_name: rule.RuleName,
                })
            }
            acc.push({
                queue_id: queue.queue_id,
                queue_name: queue.q_name,
                queue_rule_id: QUEUE_RULE_NO_RULE,
                queue_rule_name: QUEUE_RULE_NO_RULE,
            })
            return acc
        }, [])
    })

    const queueTableDataById = computed<Map<number | string, QueueData>>(() => {
        const map = new Map<number | string, QueueData>()
        for (const item of queueTableData.value) {
            map.set(item.queue_id, item)
        }
        return map
    })

    const queueTableDataByCompoundKey = computed<Map<string, QueueData>>(() => {
        const map = new Map<string, QueueData>()
        if (!isRulesMode.value) return map
        for (const item of queueTableData.value) {
            const ruleId = item.RuleID
            map.set(`${item.queue_id}_${normalizeRuleId(ruleId)}`, item)
        }
        return map
    })

    function applyStatsToRow (row: DefaultRowData, queueData: QueueData): void {
        const CallCount = queueData[multiQueueColumnsPropNames.CALL_COUNT]

        for (const key in queueData) {
            const value = queueData[key]

            if (Array.isArray(value) && key === ADDITIONAL_DATA_KEY && value.length) {
                (value as ExitCount[]).forEach((stat) => {
                    const keyName = billingCdrQueueTypes[stat.billing_cdr_queue_type]

                    if (showStatsInPercentage.value && typeof CallCount === 'number') {
                        row[keyName] = parseFloat((stat.ExitTypeCount / CallCount * 100).toFixed(2)) + '%'
                    } else {
                        row[keyName] = stat.ExitTypeCount
                    }
                })
            } else if (!Array.isArray(value) && key !== 'queue_id') {
                if (key === multiQueueColumnsPropNames.MAX_RING_TIME && typeof value === 'number') {
                    row[key] = value
                } else if (key === multiQueueColumnsPropNames.AVG_RING_TIME && typeof value === 'number' && value && typeof CallCount === 'number') {
                    row[key] = value
                } else if (showStatsInPercentage.value && typeof CallCount === 'number' && typeof value === 'number' && key !== multiQueueColumnsPropNames.CALL_COUNT && key !== multiQueueColumnsPropNames.AVG_ANSWER_HOURLY) {
                    row[key] = parseFloat((value / CallCount * 100).toFixed(2)) + '%'
                } else {
                    row[key] = value
                }
            }
        }
    }

    const mappedBaseTableData = computed<Record<number | string, DefaultRowData>>(() => {
        if (!filteredQueues.value || !filteredQueues.value.length) {
            return {}
        }

        const baseDataObject: Record<string | number, DefaultRowData> = {}
        const activeCallsByQueue = activeCallsQueueMap.value

        if (isRulesMode.value) {
            const statsMap = queueTableDataByCompoundKey.value

            allQueueRules.value.forEach((queueRule) => {
                const rowKey = `${queueRule.queue_id}_${normalizeRuleId(queueRule.queue_rule_id)}`
                const calls = activeCallsByQueue.get(queueRule.queue_id)?.Calls ?? []

                const row: DefaultRowData = {
                    ...defaultRowData,
                    queue_id: queueRule.queue_id,
                    queue_name: queueRule.queue_name,
                    queue_rule_id: queueRule.queue_rule_id,
                    queue_rule_name: queueRule.queue_rule_name
                }

                row[multiQueueColumnsPropNames.CALLERS] = calls.length
                row[multiQueueColumnsPropNames.CURRENT_WAIT_TIME] = getInitialTime(
                    calls.length
                        ? getMinValue(calls, 'JoinTimeStamp_UTC_CLIENT')
                        : new Date().getTime() * 10000
                )

                const ruleData = statsMap.get(rowKey)
                if (ruleData) {
                    applyStatsToRow(row, ruleData)
                }

                baseDataObject[rowKey] = row
            })
        } else {
            const statsById = queueTableDataById.value
            const callerCountByQueue = maxCurrentQueuesCallerCount.value

            filteredQueues.value.forEach((queue: QueueFromEntities) => {
                const row: DefaultRowData = {
                    ...defaultRowData,
                    queue_id: queue.queue_id,
                    queue_name: queue.q_name,
                }

                const calls = activeCallsByQueue.get(queue.queue_id)?.Calls ?? []

                row[multiQueueColumnsPropNames.CALLERS] = calls.length
                row[multiQueueColumnsPropNames.CURRENT_WAIT_TIME] = getInitialTime(
                    calls.length
                        ? getMinValue(calls, 'JoinTimeStamp_UTC_CLIENT')
                        : new Date().getTime() * 10000
                )
                row[multiQueueColumnsPropNames.CALL_COUNT] = callerCountByQueue[queue.queue_id]?.value ?? 0

                baseDataObject[queue.queue_id] = row

                const queueData = statsById.get(queue.queue_id)
                if (queueData) {
                    applyStatsToRow(row, queueData)
                }
            })
        }

        return baseDataObject
    })

    const queuesMaxCallers = computed(() => {
        const callersArray = Object.entries(maxCurrentQueuesCallerCount.value)
        if (!callersArray.length) {
            return null
        }
        const maxValue = callersArray.sort((a, b) => a[1].value - b[1].value).pop()
        if (!maxValue) {
            return null
        }
        return {
            queueId: maxValue[0],
            ...maxValue[1]
        }
    })

    const queuesMaxWaiting = computed(() => {
        const waitingTimeArray = Object.entries(maxCurrentQueuesWaitingTime.value)
        if (!waitingTimeArray.length) {
            return null
        }
        const maxValue = waitingTimeArray.sort((a, b) => b[1].value - a[1].value).pop()
        if (maxValue) {
            return {
                queueId: maxValue[0],
                ...maxValue[1]
            }
        }
        return null
    })

    const updateMaxCallerCount = ({ queueId, value, queueName, thresholdStatus }: MaxCallerCount) => {
        const key = typeof queueId === 'string' ? Number(queueId) : queueId
        if (isNaN(key)) return
        maxCurrentQueuesCallerCount.value = {
            ...maxCurrentQueuesCallerCount.value,
            [key]: {
                value,
                queueName,
                thresholdStatus
            }
        }
    }

    const removeQueueFromMaxCallers = (id: number | string) => {
        const key = typeof id === 'string' ? Number(id) : id
        if (isNaN(key)) return
        delete maxCurrentQueuesCallerCount.value[key]
        maxCurrentQueuesCallerCount.value = { ...maxCurrentQueuesCallerCount.value }
    }

    const updateCurrentMaxWaitTime = ({ queueId, value, thresholdStatus, queueName }: MaxWaitTime) => {
        const key = typeof queueId === 'string' ? Number(queueId) : queueId
        if (isNaN(key)) return
        maxCurrentQueuesWaitingTime.value = {
            ...maxCurrentQueuesWaitingTime.value,
            [key]: {
                value,
                queueName,
                thresholdStatus
            }
        }
    }

    const removeQueueFromMaxWaitTime = (id: number | string) => {
        const key = typeof id === 'string' ? Number(id) : id
        if (isNaN(key)) return
        delete maxCurrentQueuesWaitingTime.value[key]
        maxCurrentQueuesWaitingTime.value = { ...maxCurrentQueuesWaitingTime.value }
    }

    const getCallersStatusClass = (id: number | string): string => {
        const key = typeof id === 'string' ? Number(id) : id
        if (isNaN(key)) return ''
        return maxCurrentQueuesCallerCount.value?.[key]?.thresholdStatus
            ? `is--callers-status-${maxCurrentQueuesCallerCount.value[key].thresholdStatus}`
            : ''
    }

    const getWaitTimeStatusClass = (id: number | string): string => {
        const key = typeof id === 'string' ? Number(id) : id
        if (isNaN(key)) return ''
        return maxCurrentQueuesWaitingTime.value?.[key]?.thresholdStatus
            ? `is--waiting-status-${maxCurrentQueuesWaitingTime.value[key].thresholdStatus}`
            : ''
    }

    const getColumnTextColor = (prop: string | number): string => {
        return getMultiQueueStatTextColor(prop, widgetData.value.WidgetLayout?.colors)
    }

    const updateCurrentNotification = (id: string, queueName: string, value: number, type: string) => {
        const notificationData = {
            notification_value: type === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
            entity_name: queueName,
            current_value: `${value}.`,
            property: type === limitThresholdValue ? callersLimitProperty : callersWarningProperty
        }
        const notificationHtml = i18n.global.t(notificationTag, { ...notificationData })
        updateSoundCurrentValue(id, notificationHtml)
    }

    const fetchEntityQueues = async () => {
        allEntityQueues.value = await getOptionsList(QUEUE_LIST_KEY)
    }

    watch(
        () => [maximumWarningCallersValueFromSettings.value, maximumLimitCallersValueFromSettings.value] as const,
        ([warn, limit]) => {
            if (!warn && !limit) {
                resetCallersThresholdTracking(maxCurrentQueuesCallerCount, callersStatus)
            }
        }
    )

    watch(() => queuesMaxCallers.value, (newValue, oldValue) => {
        if (!maximumWarningCallersValueFromSettings.value && !maximumLimitCallersValueFromSettings.value) {
            resetCallersThresholdTracking(maxCurrentQueuesCallerCount, callersStatus)
            return
        }

        if (!newValue && oldValue) {
            removeSoundsFromQueueByIdWithHistoryKeep(`${callersBaseNotificationId}${oldValue.queueId}`)
            callersStatus.value = idleStatus
            return
        }

        if (!newValue) {
            return
        }

        const newId = `${callersBaseNotificationId}${newValue.queueId}`
        let oldId = ''

        if (oldValue) {
            oldId = `${callersBaseNotificationId}${oldValue.queueId}`
        }

        if (newId === oldId && newValue.thresholdStatus === callersStatus.value) {
            updateCurrentNotification(newId, newValue.queueName, newValue.value, newValue.thresholdStatus)
            return
        } else if (oldId && newId !== oldId || newValue.thresholdStatus !== callersStatus.value) {
            removeSoundsFromQueueByIdWithHistoryKeep(oldId)
        }

        let playedSound = false
        if (newValue.thresholdStatus === callersStatus.value || (callersStatus.value === limitStatus && newValue.thresholdStatus === warningStatus)) {
            playedSound = true
        }
        if (newValue.value) {
            callersStatus.value = newValue.thresholdStatus
            setupNotification({
                thresholdValue: callersStatus.value === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
                entityName: newValue.queueName,
                soundPlayed: playedSound,
                type: callersStatus.value,
                id: newId,
                currentValue: newValue.value,
                notificationTag,
                property: callersStatus.value === limitThresholdValue ? callersLimitProperty : callersWarningProperty
            })
        }
    }, {
        immediate: true
    })

    watch(
        () => [maximumWarningCallersSound.value, maximumLimitCallersSound.value],
        ([newWarningSound, newLimitSound], [oldWarningSound, oldLimitSound]) => {
            const currentCallers = queuesMaxCallers.value
            if (!currentCallers) return

            const isActiveSoundChanged =
                (currentCallers.thresholdStatus === warningThresholdValue && newWarningSound !== oldWarningSound) ||
                (currentCallers.thresholdStatus === limitThresholdValue && newLimitSound !== oldLimitSound)
            if (!isActiveSoundChanged) return

            const currentId = `${callersBaseNotificationId}${currentCallers.queueId}`
            removeSoundsFromQueueByIdWithHistoryKeep(currentId)

            setupNotification({
                thresholdValue: currentCallers.thresholdStatus === limitThresholdValue ? maximumLimitCallersValueFromSettings.value : maximumWarningCallersValueFromSettings.value,
                entityName: currentCallers.queueName,
                soundPlayed: true,
                type: currentCallers.thresholdStatus,
                id: currentId,
                currentValue: currentCallers.value,
                notificationTag,
                property: currentCallers.thresholdStatus === limitThresholdValue ? callersLimitProperty : callersWarningProperty
            })
        }
    )

    watch(
        () => [maximumWarningWaitTimeValueFromSettings.value, maximumLimitWaitTimeValueFromSettings.value] as const,
        ([warn, limit]) => {
            if (!warn && !limit) {
                resetWaitTimeThresholdTracking(maxCurrentQueuesWaitingTime, waitingQueueStatus)
            }
        }
    )

    watch(() => queuesMaxWaiting.value, (newValue, oldValue) => {
        if (!newValue && !oldValue) return

        if (!maximumWarningWaitTimeValueFromSettings.value && !maximumLimitWaitTimeValueFromSettings.value) {
            resetWaitTimeThresholdTracking(maxCurrentQueuesWaitingTime, waitingQueueStatus)
            return
        }

        const newId = newValue ? `${waitTimeNotificationId}${newValue.queueId}` : ''
        const oldId = oldValue ? `${waitTimeNotificationId}${oldValue.queueId}` : ''

        if (!newValue) {
            removeSoundsFromQueueByIdWithHistoryKeep(oldId)
            waitingQueueStatus.value = idleStatus
            return
        }

        const sameIdAndStatus = newId === oldId && newValue.thresholdStatus === waitingQueueStatus.value
        if (sameIdAndStatus && newValue.value === oldValue?.value) return

        if (sameIdAndStatus && newValue.value !== oldValue?.value) {
            updateTimerStateValue(newId, newValue.value)
            return
        }

        if (oldId && (newId !== oldId || newValue.thresholdStatus !== waitingQueueStatus.value)) {
            removeSoundsFromQueueByIdWithHistoryKeep(oldId)
        }

        const soundPlayed = newValue.thresholdStatus === waitingQueueStatus.value || (newValue.thresholdStatus === warningThresholdValue && waitingQueueStatus.value === limitThresholdValue)
        if (newValue.value) {
            waitingQueueStatus.value = newValue.thresholdStatus

            setupNotification({
                thresholdValue: timeFormatter(
                    waitingQueueStatus.value === limitThresholdValue
                        ? maximumLimitWaitTimeValueFromSettings.value
                        : maximumWarningWaitTimeValueFromSettings.value
                ),
                entityName: newValue.queueName,
                soundPlayed,
                type: waitingQueueStatus.value,
                id: newId,
                timerStartValue: newValue.value,
                currentValue: '',
                notificationTag,
                property: waitingQueueStatus.value === limitThresholdValue
                    ? waitTimeLimitProperty
                    : waitTimeWarningProperty
            })
        }
    }, {
        immediate: true
    })

    watch(
        () => [maximumWarningMaxRingTimeSound.value, maximumLimitMaxRingTimeSound.value],
        ([newWarningSound, newLimitSound], [oldWarningSound, oldLimitSound]) => {
            const currentWaiting = queuesMaxWaiting.value
            if (!currentWaiting) return

            const isActiveSoundChanged =
                (currentWaiting.thresholdStatus === warningThresholdValue && newWarningSound !== oldWarningSound) ||
                (currentWaiting.thresholdStatus === limitThresholdValue && newLimitSound !== oldLimitSound)
            if (!isActiveSoundChanged) return

            const currentId = `${waitTimeNotificationId}${currentWaiting.queueId}`
            removeSoundsFromQueueByIdWithHistoryKeep(currentId)

            setupNotification({
                thresholdValue: timeFormatter(
                    currentWaiting.thresholdStatus === limitThresholdValue
                        ? maximumLimitWaitTimeValueFromSettings.value
                        : maximumWarningWaitTimeValueFromSettings.value
                ),
                entityName: currentWaiting.queueName,
                soundPlayed: true,
                type: currentWaiting.thresholdStatus,
                id: currentId,
                timerStartValue: currentWaiting.value,
                currentValue: '',
                notificationTag,
                property: currentWaiting.thresholdStatus === limitThresholdValue
                    ? waitTimeLimitProperty
                    : waitTimeWarningProperty
            })
        }
    )

    fetchEntityQueues()

    onBeforeUnmount(() => {
        const callers = queuesMaxCallers.value
        if (callers) {
            removeSoundsFromQueueByIdWithHistoryKeep(`${callersBaseNotificationId}${callers.queueId}`)
        }
        const waiting = queuesMaxWaiting.value
        if (waiting) {
            removeSoundsFromQueueByIdWithHistoryKeep(`${waitTimeNotificationId}${waiting.queueId}`)
        }
    })

    return {
        mappedBaseTableData,
        filteredQueues,
        allQueueRules,

        queuesMaxCallers,
        queuesMaxWaiting,
        maxCurrentQueuesCallerCount,
        maxCurrentQueuesWaitingTime,

        maximumWarningCallersValueFromSettings,
        maximumLimitCallersValueFromSettings,
        maximumWarningWaitTimeValueFromSettings,
        maximumLimitWaitTimeValueFromSettings,

        updateMaxCallerCount,
        removeQueueFromMaxCallers,
        updateCurrentMaxWaitTime,
        removeQueueFromMaxWaitTime,
        getCallersStatusClass,
        getWaitTimeStatusClass,
        getColumnTextColor
    }
}


